"use client";

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefObject } from 'react';

import svgPaths from "../imports/svg-n7w9hotcin";
import svgInputPaths from "../imports/svg-tdzfjk767x";
import svgHelpPaths from "../imports/svg-xpfbbj0czi";
import FileUploadPortal from './FileUploadPortal';
import FileUploadPreview from './FileUploadPreview';
import { useSession } from './SessionDropdown';
import { useLanguage, isSupportedLanguage } from './LanguageDropdown';
import { fetchSessionById, updateMessageLikeStatus, updateMessageDislikeStatus, deleteMessageById } from '../api';
import type { completionFunctionalToolsCallsLogType, SessionContextType, SessionSummary, SessionRenameOptions, SessionUsageInfo } from '../api.types';
import { formatMessageDuration, formatMessageTimestamp } from '../utils/chatFormatters';
import markdown2html from '../utils/markdown2html';

type MessageActionKey = 'like' | 'dislike' | 'delete';
type MessageActionLoadingMap = Record<string, Record<MessageActionKey, boolean>>;

import {
  chatFlow,
  getWritingSuggestions,
  ChatFlowMessage,
  UserMessage,
  HelpMessage,
  ProceedMessage,
  Message,
  ChatActions,
  handleUserResponse as chatHandleUserResponse,
  handleSkipQuestion as chatHandleSkipQuestion,
  handleHelpClick as chatHandleHelpClick,
  handleHelpSuggestionClick as chatHandleHelpSuggestionClick,
  handleProceed as chatHandleProceed,
  handleSendFile as chatHandleSendFile,
  formatFileSize
} from '../chat';

const shouldFormatAsHtml = (message: Message): boolean => {
  return message.type !== 'user' && message.type !== 'error';
};

const formatMessageHtml = (message: Message, overrideContent?: string): string => {
  if (!message || typeof (overrideContent ?? message.content) !== 'string') {
    return '';
  }

  if (!shouldFormatAsHtml(message)) {
    return '';
  }

  return markdown2html(overrideContent ?? message.content);
};

type ChatInterfaceProps = {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  updateBrief: (field: string, value: string) => void;
  isMobile?: boolean;
  authToken?: string;
  allowedTools?: string[];
  setLastToolsCalls?: (calls: completionFunctionalToolsCallsLogType[]) => void;
  setTotalTokens?: (tokens: number) => void;
  onSessionContextChange?: (context: SessionContextType | null) => void;
  onSessionContextHistoryChange?: (history: SessionContextType[]) => void;
  onSessionSummaryChange?: (summary: SessionSummary | null) => void;
  onSessionRenameOptionsChange?: (renameOptions: SessionRenameOptions | null) => void;
  onSessionUsageChange?: (usage: SessionUsageInfo | null) => void;
};

export default function ChatInterface({
  currentStep,
  setCurrentStep,
  updateBrief,
  isMobile = false,
  authToken,
  allowedTools = [],
  setLastToolsCalls,
  setTotalTokens,
  onSessionContextChange,
  onSessionContextHistoryChange,
  onSessionSummaryChange,
  onSessionRenameOptionsChange,
  onSessionUsageChange
}: ChatInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([chatFlow[0]]);
  const [userInput, setUserInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHelpSuggestions, setShowHelpSuggestions] = useState(false);
  const [sendButtonAnimation, setSendButtonAnimation] = useState(false);
  const [showCompletionButtons, setShowCompletionButtons] = useState(false);
  const [showFileUploadMenu, setShowFileUploadMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [messageActionLoading, setMessageActionLoading] = useState<MessageActionLoadingMap>({});
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [typingState, setTypingState] = useState<{ id: string; content: string } | null>(null);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentButtonRef = useRef<HTMLButtonElement | null>(null);
  const typedMessageIdsRef = useRef<Set<string>>(new Set());
  const isHydratingSessionRef = useRef(false);
  const { selectedSession, setSelectedSession } = useSession();
  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  const updateActionLoading = useCallback((messageId: string, action: MessageActionKey, value: boolean) => {
    setMessageActionLoading((prev) => {
      const current = prev[messageId] || { like: false, dislike: false, delete: false };
      return {
        ...prev,
        [messageId]: {
          ...current,
          [action]: value
        }
      };
    });
  }, []);

  //const isChatFinished = currentStep >= chatFlow.length - 1;
  //const isChatFinished = currentStep >= 20 || chatFlow[currentStep].isFinished || false; // never finish the chat
  const isChatFinished =  false;
  //console.log("isChatFinished", isChatFinished, currentStep, chatFlow.length);

  // Create chat actions object for the business logic functions
  const chatActions: ChatActions = {
    setMessages,
    getMessages: () => messages,
    setUserInput,
    setCurrentStep,
    updateBrief,
    setSendButtonAnimation,
    setShowHelpSuggestions,
    setShowCompletionButtons,
    setIsLoading,
    focusInput: () => inputRef.current?.focus(),
    getAuthToken: () => authToken || localStorage.getItem('authToken'),
    getAllowedTools: () => allowedTools,
    getSessionId: () => selectedSession?.id,
    getLanguage: () => selectedLanguage,
    setSessionFromResponse: (session) => setSelectedSession(session),
    setLastToolsCalls: (calls) => {
      if (setLastToolsCalls) {
        setLastToolsCalls(calls as completionFunctionalToolsCallsLogType[]);
      }
    },
    setTotalTokens: (t: number) => { setTotalTokens && setTotalTokens(t); }
  };

  // Add the next message when currentStep changes
  useEffect(() => {
    if (currentStep < chatFlow.length) {
      const currentFlowMessage = chatFlow[currentStep];
      setShowSuggestions(Boolean(currentFlowMessage?.suggestions && currentFlowMessage.suggestions.length > 0));
    } else {
      setShowSuggestions(false);
    }
    setShowHelpSuggestions(false);
  }, [currentStep]);

  useEffect(() => {
    if (!copiedMessageId) {
      return;
    }

    const timeout = setTimeout(() => setCopiedMessageId(null), 2000);
    return () => clearTimeout(timeout);
  }, [copiedMessageId]);

  // Hydrate chat when a session is selected
  useEffect(() => {
    const loadSelectedSession = async () => {
      if (!selectedSession?.id) {
        onSessionContextChange && onSessionContextChange(null);
        onSessionContextHistoryChange && onSessionContextHistoryChange([]);
        onSessionSummaryChange && onSessionSummaryChange(null);
        onSessionRenameOptionsChange && onSessionRenameOptionsChange(null);
        onSessionUsageChange && onSessionUsageChange(null);
        return;
      }
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const data = await fetchSessionById(token, selectedSession.id, selectedLanguage);

        // Set token usage from the loaded session
        setTotalTokens && setTotalTokens(data.session.totalTokens);
        setLastToolsCalls && setLastToolsCalls(data.lastToolsCalls as completionFunctionalToolsCallsLogType[]);
        onSessionContextChange && onSessionContextChange(data.session.current_context ? {
          websiteId: data.session.current_context.websiteId ?? null,
          pageName: data.session.current_context.pageName ?? null
        } : null);
        onSessionContextHistoryChange && onSessionContextHistoryChange(
          (data.session.context_history || []).map((entry) => ({
            websiteId: entry.websiteId ?? null,
            pageName: entry.pageName ?? null
          }))
        );
        onSessionSummaryChange && onSessionSummaryChange(data.session.summary ?? null);
        onSessionRenameOptionsChange && onSessionRenameOptionsChange(data.session.renameOptions ?? null);
        onSessionUsageChange && onSessionUsageChange(data.session.usage ?? null);

        // Map backend messages to UI messages
        const mapped = data.session.messages.map((m) => {
          const isUser = m.role === 'user';
          return {
            id: m.id,
            type: isUser ? 'user' : 'system',
            role: m.role,
            content: String(m.content ?? ''),
            timestamp: m.timestamp,
            duration: m.duration,
            isLiked: m.is_liked,
            isDisliked: m.is_disliked
          } as Message;
        });

        // Replace messages with those from the selected session only
        isHydratingSessionRef.current = true;
        setMessages(mapped);
        typedMessageIdsRef.current = new Set(mapped.map((msg) => String(msg.id)));

        const sessionLanguage = data.session.language;
        if (sessionLanguage && isSupportedLanguage(sessionLanguage)) {
          setSelectedLanguage(sessionLanguage);
        }

        // Reset current step within chatFlow bounds so effect that appends AI message runs
        // We keep currentStep aligned to chatFlow, not to the number of historic messages
        setCurrentStep(Math.max(0, Math.min(chatFlow.length - 1, mapped.length)));
        setTimeout(() => {
          isHydratingSessionRef.current = false;
        }, 0);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session', e);
        onSessionContextChange && onSessionContextChange(null);
        onSessionContextHistoryChange && onSessionContextHistoryChange([]);
        onSessionUsageChange && onSessionUsageChange(null);
      }
    };
    loadSelectedSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id]);

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    if (typedMessageIdsRef.current.size === 0) {
      typedMessageIdsRef.current = new Set(messages.map((msg) => String(msg.id)));
    }
  }, [messages]);

  useEffect(() => {
    if (!messages.length || isHydratingSessionRef.current) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const lastMessageId = String(lastMessage.id);

    if (lastMessage.type !== 'system') {
      return;
    }

    if (typedMessageIdsRef.current.has(lastMessageId)) {
      return;
    }

    setTypingMessageId(lastMessageId);
    setTypingState({ id: lastMessageId, content: '' });
  }, [messages]);

  useEffect(() => {
    if (!typingMessageId) {
      return;
    }

    const activeMessage = messages.find((msg) => String(msg.id) === typingMessageId);
    if (!activeMessage || typeof activeMessage.content !== 'string') {
      setTypingMessageId(null);
      setTypingState(null);
      return;
    }

    const fullText = activeMessage.content;
    if (!fullText.length) {
      typedMessageIdsRef.current.add(typingMessageId);
      setTypingMessageId(null);
      setTypingState(null);
      return;
    }

    let index = 0;
    const maxDelay = 30;
    const minDelay = 10;
    const dynamicDelay = Math.floor(800 / Math.max(20, fullText.length));
    const intervalDelay = Math.min(maxDelay, Math.max(minDelay, dynamicDelay));

    const interval = setInterval(() => {
      index = Math.min(fullText.length, index + 2);
      setTypingState({ id: typingMessageId, content: fullText.slice(0, index) });

      if (index >= fullText.length) {
        clearInterval(interval);
        typedMessageIdsRef.current.add(typingMessageId);
        setTypingMessageId(null);
        setTypingState(null);
      }
    }, intervalDelay);

    return () => clearInterval(interval);
  }, [messages, typingMessageId]);

  // Scroll to bottom of messages with a buffer space above the input
  useEffect(() => {
    if (messagesEndRef.current) {
      const scrollContainer = chatContainerRef.current;
      if (scrollContainer) {
        // Add a delay to ensure animations have completed
        setTimeout(() => {
          messagesEndRef.current && messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [messages, selectedFile]);

  // Close file upload menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showFileUploadMenu &&
        attachmentButtonRef.current &&
        event.target instanceof Node &&
        !attachmentButtonRef.current.contains(event.target)
      ) {
        setShowFileUploadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFileUploadMenu]);

  // We're no longer calculating menu position here since FileUploadPortal handles it
  useEffect(() => {
    // Clear any console logs or debugging when menu visibility changes
    if (showFileUploadMenu) {
      console.log("Showing file upload menu");
    }
  }, [showFileUploadMenu]);

  const handleUserResponse = async (response: string | null = null) => {
    await chatHandleUserResponse(chatActions, currentStep, userInput, response);
  };

  const handleSendFile = () => {
    if (selectedFile) {
      chatHandleSendFile(chatActions, selectedFile, setSelectedFile);
    }
  };

  const handleSkipQuestion = () => {
    chatHandleSkipQuestion(chatActions);
  };

  const handleHelpClick = () => {
    chatHandleHelpClick(chatActions, currentStep);
  };

  const handleHelpSuggestionClick = (suggestion: string) => {
    chatHandleHelpSuggestionClick(chatActions, suggestion);
  };

  const handleProceed = () => {
    chatHandleProceed(chatActions);
  };

  const handleCopyMessage = useCallback(async (message: Message) => {
    if (!message || typeof message.content !== 'string') {
      return;
    }

    const plainText = message.type === 'user'
      ? message.content
      : message.content
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/?[^>]+(>|$)/g, '');

    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(plainText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedMessageId(String(message.id));
    } catch (error) {
      console.error('Failed to copy message', error);
    }
  }, []);

  const handleToggleReaction = useCallback(async (message: Message, action: MessageActionKey) => {
    if (!message) {
      return;
    }

    const messageId = typeof message.id === 'string' ? message.id : null;
    if (!messageId) {
      return;
    }

    const sessionId = selectedSession?.id;
    if (!sessionId) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for message reaction');
      return;
    }

    const currentLike = Boolean(message.isLiked);
    const currentDislike = Boolean(message.isDisliked);
    const shouldActivate = action === 'like' ? !currentLike : !currentDislike;
    const previousState = { isLiked: currentLike, isDisliked: currentDislike };

    updateActionLoading(messageId, action, true);

    setMessages((prev: Message[]) => prev.map((msg) => {
      if (msg.id !== message.id) {
        return msg;
      }

      if (action === 'like') {
        return {
          ...msg,
          isLiked: shouldActivate,
          isDisliked: shouldActivate ? false : msg.isDisliked
        };
      }

      return {
        ...msg,
        isDisliked: shouldActivate,
        isLiked: shouldActivate ? false : msg.isLiked
      };
    }));

    try {
      if (action === 'like') {
        await updateMessageLikeStatus(token, sessionId, messageId, shouldActivate);
        if (shouldActivate && previousState.isDisliked) {
          await updateMessageDislikeStatus(token, sessionId, messageId, false);
        }
      } else {
        await updateMessageDislikeStatus(token, sessionId, messageId, shouldActivate);
        if (shouldActivate && previousState.isLiked) {
          await updateMessageLikeStatus(token, sessionId, messageId, false);
        }
      }
    } catch (error) {
      console.error('Failed to update message reaction', error);
      setMessages((prev: Message[]) => prev.map((msg) => {
        if (msg.id !== message.id) {
          return msg;
        }

        return {
          ...msg,
          isLiked: previousState.isLiked,
          isDisliked: previousState.isDisliked
        };
      }));
    } finally {
      updateActionLoading(messageId, action, false);
    }
  }, [authToken, selectedSession?.id, updateActionLoading]);

  const handleDeleteMessage = useCallback(async (message: Message) => {
    if (!message) {
      return;
    }

    const messageId = typeof message.id === 'string' ? message.id : null;
    if (!messageId) {
      return;
    }

    const sessionId = selectedSession?.id;
    if (!sessionId) {
      return;
    }

    if (!window.confirm('Delete this message?')) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for message deletion');
      return;
    }

    updateActionLoading(messageId, 'delete', true);

    try {
      await deleteMessageById(token, sessionId, messageId);
      setMessages((prev: Message[]) => {
        const targetIndex = prev.findIndex((msg) => msg.id === message.id);
        if (targetIndex === -1) {
          return prev;
        }

        return prev.slice(0, targetIndex);
      });
    } catch (error) {
      console.error('Failed to delete message', error);
    } finally {
      updateActionLoading(messageId, 'delete', false);
    }
  }, [authToken, selectedSession?.id, updateActionLoading]);

  const handleAttachmentClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent event bubbling
    // Toggle file upload menu
    setShowFileUploadMenu(!showFileUploadMenu);
    console.log("Attachment clicked, showing menu:", !showFileUploadMenu);
  };

  const handleFileTypeSelect = (fileType: { extensions: string[] }) => {
    setShowFileUploadMenu(false);

    // Set accepted file types for the input
    if (fileInputRef.current) {
      const input = fileInputRef.current as HTMLInputElement;
      if (fileType.extensions[0] === '*') {
        input.accept = '';
      } else {
        input.accept = fileType.extensions.join(',');
      }
      input.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    // Simulate file upload
    setIsUploading(true);
    setUploadProgress(0);

    const simulation = setInterval(() => {
      setUploadProgress((prev: number) => {
        if (prev >= 100) {
          clearInterval(simulation);
          setTimeout(() => {
            setIsUploading(false);
            setSelectedFile(file);
          }, 500);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  // Calculate bottom spacing based on whether suggestions are shown
  const bottomSpacing = showSuggestions
    ? isMobile ? "260px" : "280px"
    : isMobile ? "160px" : "180px";

  // Determine if a message is the current step's question message
  const isCurrentQuestion = (message: Message) => {
    return (
      currentStep > 0 &&
      currentStep < chatFlow.length - 1 &&
      message.id === chatFlow[currentStep].id &&
      message.type === 'question'
    );
  };

  return (
    <motion.div
      className="flex flex-col h-full relative w-full max-w-[1078px] mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Messages Container with Proper Padding */}
      <div
        className={`flex-1 overflow-y-auto overflow-x-hidden px-12 py-8 chat-container-appear custom-scrollbar ${isMobile ? 'mobile-chat-window' : ''} w-full`}
        ref={chatContainerRef}
        style={{ paddingBottom: bottomSpacing }}
      >
        <div className={`${isMobile ? 'w-full' : 'max-w-3xl'} mx-auto`}>
          <AnimatePresence mode="popLayout">
            {messages.map((message, index) => {
              const messageKey = String(message.id);
              const backendMessageId = typeof message.id === 'string' ? message.id : undefined;
              const actionState = backendMessageId ? messageActionLoading[backendMessageId] : undefined;
              const isCopied = copiedMessageId === messageKey;
              const isErrorMessage = message.type === 'error';
              const actionsDisabled = !selectedSession?.id || typeof message.id !== 'string' || messageKey.startsWith('error-') || isErrorMessage || (typeof message.id === 'string' && message.id.startsWith('user-'));
              const typedContent = typingState?.id === messageKey ? typingState.content : undefined;
              const htmlContent = formatMessageHtml(message, typedContent);

              return (
                <motion.div
                  key={messageKey}
                  data-message-id={backendMessageId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`mb-8 ${message.type === 'user' ? 'flex justify-end' : 'flex justify-start'} max-w-full`}
                >
                  {message.type === 'user' ? (
                    <div className="flex items-end gap-3">
                      <motion.div
                        className="bg-[#eaf0ff] p-4 rounded-tl-lg rounded-tr-lg rounded-bl-lg message-user-bubble break-words max-w-full"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        {message.file ? (
                          <div className="flex items-center gap-3">
                            <div className="bg-white rounded-md p-2 flex-shrink-0">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM13 9V3.5L18.5 9H13Z"
                                  fill="#4b81ff"
                                />
                              </svg>
                            </div>
                            <div className="text-[#333333] text-[16px]">
                              <p className="font-medium">{message.file.name}</p>
                              <p className="text-[14px] text-[#666666]">{message.file.size}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-[#333333] text-[16px] text-center">
                            <p>{message.content}</p>
                          </div>
                        )}
                        <MessageActionBar
                          message={message}
                          onCopy={handleCopyMessage}
                          onToggleReaction={handleToggleReaction}
                          onDelete={handleDeleteMessage}
                          loading={actionState}
                          isCopied={isCopied}
                          actionsDisabled={actionsDisabled}
                        />
                      </motion.div>
                      <motion.div
                        className="bg-[#464c5c] rounded-full size-10 flex items-center justify-center"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <p className="font-bold text-[#f7f8fa] text-[16px]">DL</p>
                      </motion.div>
                    </div>
                  ) : message.type === 'error' ? (
                    <div className="flex flex-col">
                      <div className="flex items-start gap-3">
                        {/* Check if this is a limit exceeded error - display with warning style */}
                        {message.content.includes('Limit Exceeded') ? (
                          <>
                            <motion.div
                              className="rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1 bg-[#fef3c7]"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.1 }}
                            >
                              <div className="size-6">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#f59e0b"/>
                                </svg>
                              </div>
                            </motion.div>
                            <div className="flex flex-col max-w-full sm:max-w-[598px]">
                              <motion.div
                                className="bg-[#fffbeb] p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(245,158,11,0.3)] message-warning-bubble break-words max-w-full border border-[#fde68a]"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <div className="text-[#92400e] text-[16px] whitespace-pre-line">
                                  <p>{message.content}</p>
                                </div>
                                <MessageActionBar
                                  message={message}
                                  onCopy={handleCopyMessage}
                                  onToggleReaction={handleToggleReaction}
                                  onDelete={handleDeleteMessage}
                                  loading={actionState}
                                  isCopied={isCopied}
                                  actionsDisabled={actionsDisabled}
                                />
                              </motion.div>
                            </div>
                          </>
                        ) : (
                          <>
                            <motion.div
                              className="rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1 bg-[#fff0f0]"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.1 }}
                            >
                              <div className="size-6">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#E53935"/>
                                </svg>
                              </div>
                            </motion.div>
                            <div className="flex flex-col max-w-full sm:max-w-[598px]">
                              <motion.div
                                className="bg-[#fff0f0] p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(229,57,53,0.3)] message-error-bubble break-words max-w-full border border-[#ffcdd2]"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <div className="text-[#E53935] text-[16px]">
                                  <p>{message.content}</p>
                                </div>
                                <MessageActionBar
                                  message={message}
                                  onCopy={handleCopyMessage}
                                  onToggleReaction={handleToggleReaction}
                                  onDelete={handleDeleteMessage}
                                  loading={actionState}
                                  isCopied={isCopied}
                                  actionsDisabled={actionsDisabled}
                                />
                              </motion.div>
                              <motion.div
                                className="mt-2 ml-2"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                              >
                                <button
                                  className="text-[#165BFB] text-[14px] font-medium flex items-center gap-1.5"
                                  onClick={() => {
                                    const lastUserMessage = messages
                                      .filter(msg => msg.type === 'user')
                                      .pop();

                                    if (lastUserMessage) {
                                      handleUserResponse(lastUserMessage.content).catch(err =>
                                        console.error('Error retrying user response:', err)
                                      );
                                    }
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="#165BFB"/>
                                  </svg>
                                  Try again
                                </button>
                              </motion.div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="flex items-start gap-3">
                        <motion.div
                          className="rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1"
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          <AssistantIcon messagesLength={messages.length} />
                        </motion.div>
                        <div className="flex flex-col max-w-full sm:max-w-[598px]">
                          <motion.div
                            className="bg-white p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(145,153,171,0.3)] message-ai-bubble break-words max-w-full"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                          <div className="text-[#434343] text-[16px] chat-rich-text">
                            <p dangerouslySetInnerHTML={{ __html: htmlContent }}></p>
                            </div>
                            <MessageActionBar
                              message={message}
                              onCopy={handleCopyMessage}
                              onToggleReaction={handleToggleReaction}
                              onDelete={handleDeleteMessage}
                              loading={actionState}
                              isCopied={isCopied}
                              actionsDisabled={actionsDisabled}
                            />
                          </motion.div>

                          {message.id === "7" && showCompletionButtons && (
                            <motion.div
                              className="mt-4 flex flex-wrap gap-3"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                            >
                              <motion.button
                                className="bg-[#165bfb] text-white rounded-[32px] px-6 py-3 text-[14px] font-medium flex items-center justify-center completion-button-primary"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.6 }}
                                onClick={handleProceed}
                              >
                                Proceed
                              </motion.button>
                              <motion.button
                                className="bg-white border border-[#edf0f5] text-[#464c5c] rounded-[32px] px-6 py-3 text-[14px] font-medium flex items-center justify-center completion-button-secondary"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.7 }}
                                onClick={() => setCurrentStep(1)}
                              >
                                Continue briefing
                              </motion.button>
                            </motion.div>
                          )}

                          {message.type === 'help' && message.suggestions && (
                            <motion.div
                              className="mt-4 flex flex-wrap gap-2"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.3 }}
                            >
                              {message.suggestions.map((suggestion, idx) => (
                                <motion.div
                                  key={String(suggestion)}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.3 + (idx * 0.1) }}
                                >
                                  <SuggestionButton
                                    text={suggestion}
                                    onClick={() => handleHelpSuggestionClick(suggestion)}
                                  />
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* File upload preview if a file is selected */}
          <AnimatePresence>
            {selectedFile && (
              <div className={`mt-2 mb-4 ${isMobile ? 'w-full' : 'mx-auto max-w-md'}`}>
                <FileUploadPreview
                  file={selectedFile}
                  onRemove={handleRemoveFile}
                  onSend={handleSendFile}
                />
              </div>
            )}
          </AnimatePresence>

          {/* Spacer element to maintain scroll distance from input */}
          <div className="h-4" ref={messagesEndRef}></div>
        </div>
      </div>

      {/* Fixed Input Area with Buffer Space and Gradient Fadeout */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        {/* Gradient fadeout at the bottom to visually indicate messages approaching input */}
        <div className={`absolute bottom-0 left-0 right-0 h-[120px] bg-gradient-to-t from-white via-white to-transparent z-10 ${isMobile ? 'mobile-fadeout' : ''}`}></div>

        {/* Input Component */}
        <motion.div
          className={`relative mx-auto bottom-[52px] w-full max-w-[680px] flex items-center gap-4 z-20 pointer-events-auto ${isMobile ? 'mobile-input-container' : ''}`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 400, damping: 25 }}
        >
          {/* Main Input Container */}
          <motion.div
            className="bg-[#ffffff] h-16 relative rounded-2xl flex-grow shadow-lg"
            whileHover={{ boxShadow: "0px 4px 15px rgba(22, 91, 251, 0.1)" }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-16 overflow-clip relative w-full">
              {/* Placeholder text */}
              {!userInput && !selectedFile && (
                <motion.div
                  className={`absolute top-1/2 -translate-y-1/2 text-[#a4a8b3] text-[16px] ${isMobile ? 'left-16 text-[14px]' : 'left-20'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <p>{isMobile ? 'Write or upload files...' : 'Write an answer or upload files...'}</p>
                </motion.div>
              )}

              {/* Input field */}
              <input
                ref={inputRef}
                type="text"
                value={userInput}
                onChange={(e) => {
                  if (isChatFinished) return;
                  setUserInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (isChatFinished || isLoading) return;
                  if (e.key === 'Enter' && userInput) {
                    handleUserResponse().catch(err => console.error('Error handling user response:', err));
                  }
                }}
                className={`absolute inset-0 bg-transparent border-none outline-none text-[16px] text-[#333333] ${isMobile ? 'px-16 text-[14px]' : 'px-20'} ${isChatFinished ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isChatFinished}
                aria-disabled={isChatFinished}
                tabIndex={isChatFinished ? -1 : 0}
                aria-label="Chat input"
              />

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {/* Blue Attachment button with white paperclip */}
              <motion.button
                ref={attachmentButtonRef}
                className={`absolute left-4 top-8 -translate-y-1/2 flex items-center justify-center rounded-[27.4286px] cursor-pointer file-upload-button overflow-hidden ${isMobile ? 'size-8' : 'size-9'} ${showFileUploadMenu ? 'bg-[#2c65ff]' : 'bg-[#4b81ff]'} ${isChatFinished || isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                onClick={isChatFinished || isLoading ? undefined : handleAttachmentClick}
                whileHover={isChatFinished || isLoading ? {} : { scale: 1.05 }}
                whileTap={isChatFinished || isLoading ? {} : { scale: 0.95 }}
                transition={{ duration: 0.2 }}
                aria-label="Attach file"
                disabled={isChatFinished || isLoading}
                aria-disabled={isChatFinished || isLoading}
                tabIndex={isChatFinished || isLoading ? -1 : 0}
              >
                {isUploading ? (
                  // Upload progress indicator
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div className="absolute inset-0 bg-blue-500 opacity-20"></div>
                    <div
                      className="absolute bottom-0 left-0 bg-white opacity-50 h-1"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                ) : (
                  // Normal paperclip icon
                  <div className="absolute w-[57%] h-[57%]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
                      <path
                        clipRule="evenodd"
                        d={svgInputPaths.p3beeae00}
                        fill="white"
                        fillRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </motion.button>
            </div>
            <div className="absolute border-2 border-[#ecf2ff] border-solid inset-[-2px] pointer-events-none rounded-[18px]" />
          </motion.div>

          {/* Separate Send Button */}
          <motion.div
            className={`h-16 relative rounded-2xl cursor-pointer flex items-center justify-center send-button ${isMobile ? 'w-14' : 'w-16'} ${sendButtonAnimation ? 'send-button-press' : ''} ${userInput || selectedFile ? 'bg-[#dee8ff]' : 'bg-[#f6f8ff]'} ${isLoading ? 'opacity-60 cursor-wait' : ''} ${isChatFinished ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
            onClick={() => {
              if (isChatFinished || isLoading) return;
              if (selectedFile) {
                handleSendFile();
              } else if (userInput) {
                handleUserResponse().catch(err => console.error('Error handling user response:', err));
              }
            }}
            whileHover={isChatFinished || isLoading ? {} : (userInput || selectedFile ? { scale: 1.03, boxShadow: "0px 4px 10px rgba(22, 91, 251, 0.1)" } : {})}
            whileTap={isChatFinished || isLoading ? {} : (userInput || selectedFile ? { scale: 0.95 } : {})}
            transition={{ duration: 0.2 }}
            aria-disabled={isChatFinished || isLoading}
            tabIndex={isChatFinished || isLoading ? -1 : 0}
          >
            <div className={`flex items-center justify-center ${isMobile ? 'size-6' : 'size-8'}`}>
              {isLoading ? (
                <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#A4A8B3" strokeWidth="2.5" />
                  <path d="M12 2C14.717 2 17.337 3.06458 19.2695 4.95867C21.2021 6.85276 22.3362 9.42975 22.3928 12.1455C22.4493 14.8612 21.4241 17.4813 19.5722 19.4518" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" />
                </svg>
              ) : (
                <svg className={`${isMobile ? 'size-5' : 'size-6'}`} viewBox="0 0 24 24" fill="none">
                  <path
                    d={svgInputPaths.p5b88880}
                    fill={userInput || selectedFile ? "#165BFB" : "#A4A8B3"}
                  />
                </svg>
              )}
            </div>
            <div className="absolute border-2 border-[#dee8ff] border-solid inset-[-2px] pointer-events-none rounded-[18px]" />
          </motion.div>
        </motion.div>

        {/* Suggestion buttons - positioned above the input */}
        <AnimatePresence>
          {showSuggestions && currentStep < chatFlow.length && chatFlow[currentStep].suggestions && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center w-full pointer-events-none">
              <motion.div
                className={`absolute ${isMobile ? 'bottom-[120px]' : 'bottom-[140px]'} flex gap-2 flex-wrap justify-center z-20 max-w-[680px] w-full pointer-events-auto px-4`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.4 }}
              >
                {chatFlow[currentStep].suggestions.map((suggestion, index) => (
                  <motion.button
                    key={String(suggestion)}
                    className="bg-[#165bfb] text-white rounded-[64px] px-3.5 py-2 text-[14px] suggestion-button"
                    onClick={() => handleUserResponse(suggestion).catch(err => console.error('Error handling suggestion:', err))}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + (index * 0.1) }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {suggestion}
                  </motion.button>
                ))}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Using our new FileUploadPortal component which handles positioning */}
      <FileUploadPortal
        isOpen={showFileUploadMenu}
        onClose={() => setShowFileUploadMenu(false)}
        onSelectFileType={handleFileTypeSelect}
        attachmentButtonRef={attachmentButtonRef}
        isMobile={isMobile}
      />

      {/* Loading Indicator - Non-blocking */}
      {isLoading && (
        <motion.div
          className="fixed bottom-32 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg px-5 py-3 flex items-center gap-3 z-50 loading-indicator-appear"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#EDF0F5" strokeWidth="2.5" />
            <path d="M12 2C14.717 2 17.337 3.06458 19.2695 4.95867C21.2021 6.85276 22.3362 9.42975 22.3928 12.1455C22.4493 14.8612 21.4241 17.4813 19.5722 19.4518" stroke="#165BFB" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span className="text-[#333333] font-medium">Processing...</span>
        </motion.div>
      )}
    </motion.div>
  );
}

type MessageActionBarProps = {
  message: Message;
  onCopy: (message: Message) => void;
  onToggleReaction: (message: Message, action: MessageActionKey) => void;
  onDelete: (message: Message) => void;
  loading?: Record<MessageActionKey, boolean>;
  isCopied: boolean;
  actionsDisabled: boolean;
};

function MessageActionBar({
  message,
  onCopy,
  onToggleReaction,
  onDelete,
  loading,
  isCopied,
  actionsDisabled
}: MessageActionBarProps) {
  const messageIdLabel = String(message.id ?? '');
  const timestampLabel = message.timestamp ? formatMessageTimestamp(message.timestamp) : '';
  const durationLabel = typeof message.duration === 'number' ? formatMessageDuration(message.duration) : '';

  const likeActive = Boolean(message.isLiked);
  const dislikeActive = Boolean(message.isDisliked);

  const loadingState = {
    like: loading?.like ?? false,
    dislike: loading?.dislike ?? false,
    delete: loading?.delete ?? false
  };

  const baseButtonClass = 'flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors';
  const activeButtonClass = 'bg-[#e6f0ff] border-[#165bfb] text-[#165bfb]';
  const inactiveButtonClass = 'bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#165bfb] hover:text-[#165bfb]';
  const disabledButtonClass = 'opacity-50 cursor-not-allowed pointer-events-none';
  const copyButtonClass = `${baseButtonClass} ${isCopied ? activeButtonClass : inactiveButtonClass}`;
  const infoPillClass = 'flex items-center gap-1 rounded-full bg-[#f5f7fb] px-3 py-1 text-xs text-[#4b5563]';

  const isLikeDisabled = actionsDisabled || loadingState.like;
  const isDislikeDisabled = actionsDisabled || loadingState.dislike;
  const isDeleteDisabled = actionsDisabled || loadingState.delete;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {messageIdLabel && (
        <span style={{display: 'none'}} className={infoPillClass} title={`Message ID: ${messageIdLabel}`}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 7h10v10H7z" />
            <path d="M3 7h4v10H3zM17 7h4v10h-4z" />
          </svg>
          {messageIdLabel}
        </span>
      )}

      <button
        type="button"
        className={`${baseButtonClass} ${likeActive ? activeButtonClass : inactiveButtonClass} ${isLikeDisabled ? disabledButtonClass : ''}`}
        onClick={() => onToggleReaction(message, 'like')}
        disabled={isLikeDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={likeActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s-6-4.35-6-8.5S8.686 4 12 7.5 18 4 18 12.5 12 21 12 21z" />
        </svg>
        {loadingState.like ? 'Saving...' : likeActive ? 'Liked' : 'Like'}
      </button>

      <button
        type="button"
        className={`${baseButtonClass} ${dislikeActive ? activeButtonClass : inactiveButtonClass} ${isDislikeDisabled ? disabledButtonClass : ''}`}
        onClick={() => onToggleReaction(message, 'dislike')}
        disabled={isDislikeDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={dislikeActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3s6 4.35 6 8.5S15.314 20 12 16.5 6 20 6 11.5 12 3 12 3z" />
        </svg>
        {loadingState.dislike ? 'Saving...' : dislikeActive ? 'Disliked' : 'Dislike'}
      </button>

      <button
        type="button"
        className={copyButtonClass}
        onClick={() => onCopy(message)}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        {isCopied ? 'Copied' : 'Copy'}
      </button>

      {durationLabel && (
        <span className={infoPillClass} title={`Duration: ${durationLabel}`}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 12 8 8" />
            <path d="M12 12h4" />
          </svg>
          {durationLabel}
        </span>
      )}

      {timestampLabel && (
        <span className={infoPillClass} title={timestampLabel}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          {timestampLabel}
        </span>
      )}

      <button
        type="button"
        className={`${baseButtonClass} ${inactiveButtonClass} ${isDeleteDisabled ? disabledButtonClass : ''}`}
        onClick={() => onDelete(message)}
        disabled={isDeleteDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
        {loadingState.delete ? 'Removing...' : 'Delete'}
      </button>
    </div>
  );
}

// Suggestion Button Component matching the imported Figma design
type SuggestionButtonProps = { text: string; onClick: () => void };
function SuggestionButton({ text, onClick }: SuggestionButtonProps) {
  return (
    <motion.div
      className="relative rounded-[64px] cursor-pointer suggestion-button"
      onClick={onClick}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.2 }}
    >
      <div className="absolute border-2 border-[#4b81ff] border-solid inset-0 pointer-events-none rounded-[64px]" />
      <div className="flex flex-row items-center">
        <div className="box-border content-stretch flex flex-row gap-1 items-center justify-start px-3.5 py-2">
          <div className="overflow-clip relative rounded-[5.92932px] shrink-0 size-5">
            <div className="absolute bottom-[12.5%] left-[12.5%] right-[12.5%] top-[12.5%]">
              <svg
                className="block size-full"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 15 15"
              >
                <path
                  clipRule="evenodd"
                  d={svgHelpPaths.p6b0d00}
                  fill="#165BFB"
                  fillRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div className="font-normal text-[#165bfb] text-[14px] text-nowrap">
            <p className="leading-[20px] whitespace-pre">{text}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Assistant Icon Component with Animation
type AssistantIconProps = { messagesLength: number };
function AssistantIcon({ messagesLength }: AssistantIconProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Trigger animation whenever messages change
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 800);
    return () => clearTimeout(timer);
  }, [messagesLength]);

  return (
    <motion.div
      className="bg-[#eef3ff] size-full flex items-center justify-center rounded-full"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className={`assistant-icon-container ${isAnimating ? 'animating' : ''}`}>
        {isAnimating && (
          <div className={`assistant-icon-pulse ${isAnimating ? 'animating' : ''}`} />
        )}
        <div className="size-[60%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 19 19">
            <defs>
              <linearGradient id="botIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#165BFB" />
                <stop offset="100%" stopColor="#4087FF" />
              </linearGradient>
            </defs>
            <g>
              <path d={svgPaths.p12a13a40} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
              <path d={svgPaths.pa8077f0} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
              <path d={svgPaths.p35352600} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
            </g>
          </svg>
        </div>
      </div>
    </motion.div>
  );
}
